'use client'

import { useFailedToolRuns } from '@/hooks/use-failed-tool-runs'
import { FailedToolRuns } from '@/components/marketing/tools/failed-tool-runs'
import { toolFailureMessage } from '@/lib/tool-generations'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import {
  AlertCircle,
  ArrowRight,
  Copy,
  Download,
  Maximize2,
  Loader2,
  Music,
  RefreshCw,
  Upload,
  X,
  Zap,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PricingDialog } from '@/components/billing/pricing-dialog'
import { FreePreviewResultBanner } from '@/components/marketing/tools/free-preview'
import { CreditBadge } from '@/components/marketing/tools/credit-badge'
import { useToolCreditTopUp } from '@/components/marketing/tools/use-tool-credit-top-up'
import { FalToolAuthDialog } from '@/components/marketing/tools/fal-tool-auth-dialog'
import { ToolWorkspacePicker } from '@/components/marketing/tools/tool-workspace-picker'
import { useAuthContext } from '@/hooks/use-auth-context'
import { revalidateCreditBalance } from '@/hooks/use-billing'
import { useToolWorkspaceOwner } from '@/hooks/use-tool-workspace-owner'
import {
  isAppSumoPlan,
  isTopPlan as isTopPlanSlug,
} from '@/lib/billing/top-plan'
import { useCreditStore } from '@/stores/credit-store'
import { useBillingDialogStore } from '@/stores/billing-dialog-store'
import { track } from '@/lib/analytics/track'
import { getBackendAuthToken } from '@/lib/backend-auth-token'
import { pricingDialogVariantForPagePath } from '@/lib/pricing-dialog-variant'
import { shouldEnsureToolWorkspace } from '@/lib/tool-workspace-selection'
import { assetDownloadSrc, assetMediaSrc } from '@/lib/tool-generation-src'
import { readMediaFileKind } from '@/lib/media-file-kind'
import { recordStatusPollFailure } from '@/lib/tool-generation-recovery'
import {
  falToolFormKey as formKey,
  falToolHistoryKey as historyKey,
  falToolPendingKey as pendingKey,
  mergeServerHistory,
  parseFalToolPayload,
  stampFalToolPayload,
} from '@/lib/fal-tool-storage'
import {
  clearPendingUpload,
  savePendingUpload,
  takePendingUpload,
  type PendingUploadKind,
} from '@/lib/pending-tool-upload'
import {
  computeCreditCost,
  getCachedFalToolSpec,
  historyDeliveryMode,
  loadStoredGenerations,
  useFalTool,
  UploadFailure,
  type FalToolSpec,
  type GenerateParams,
} from '@/hooks/use-fal-tool'

/**
 * The one interactive surface behind every /tools/* page.
 *
 * It renders itself from the server's spec (GET /api/tools/fal/{slug}/cost):
 * which controls exist, what they accept, what a job costs and what engine runs
 * are all decided server-side, so a page cannot advertise a control the backend
 * will reject or a price it will not charge.
 *
 * The whole funnel happens in place — sign up, top up, generate, and then hand
 * the result into the product. Every step that used to be a navigation away
 * (`/sign-up`, the billing pages) is a dialog here, because leaving the page is
 * where these visitors were being lost.
 */

/**
 * What the canvas adds, said in terms of the artifact the visitor is holding.
 *
 * This block replaces a generic "unlock advanced features" line, and the
 * difference is the whole point. A visitor who just upscaled one clip cannot
 * price "multi-model" or "agentic workflows" — they can price "run this same
 * clip through three models and keep the one that tests best", because they
 * have the clip. Each line therefore names a job that takes THIS output as its
 * input, which is also exactly what the handoff button below actually does.
 *
 * Keyed by output kind rather than by slug so a newly registered tool inherits
 * sensible copy instead of falling through to nothing.
 */
const NEXT_STEPS: Record<string, string[]> = {
  video: [
    'Branch variants off this clip — new hooks, new lengths, new aspect ratios.',
    'Re-run it through Seedance 2.5, MiniMax H3 or Topaz and keep what tests best.',
    'Assemble it with your other cuts into a finished ad, then launch it to Meta, Google or TikTok.',
  ],
  image: [
    'Turn this still into a video with image-to-video, or extend it into a full scene.',
    'Generate the rest of the set — same subject, new angles, backgrounds and formats.',
    'Drop it into an ad layout and push it live with the copy alongside it.',
  ],
  audio: [
    'Lip-sync this track onto footage so one take can carry a rewritten script.',
    'Re-read the same script in other voices and languages for per-market cuts.',
    'Lay it under a clip on the canvas and assemble the finished spot.',
  ],
  text: [
    'Take the winning angle straight into a video or image generation.',
    'Build a variant set around it and test the angles against each other.',
    'Hand it to the agent with your ad data and let it pick what to scale.',
  ],
}

/** Fallback ratio set for tools whose spec does not pin one. Models disagree
 *  (LTX reframe has 4:5 / 5:4 but no 4:3), so the spec always wins. */
const DEFAULT_ASPECT_RATIOS = ['16:9', '9:16', '1:1', '4:3', '3:4']

/**
 * The listed ratio closest to a clip's real shape.
 *
 * Used by the tools that must preserve the shot. Nearest-by-value rather than
 * exact-match because a 1080x1920 upload is 0.5625 and the label is "9:16" —
 * and because a source whose ratio the model does not offer still has a best
 * available answer, which beats silently keeping 16:9.
 */
function nearestAspectRatio(
  width: number,
  height: number,
  allowed: string[],
): string | null {
  if (!width || !height || !allowed.length) return null
  const target = width / height
  let best: string | null = null
  let bestGap = Infinity
  for (const label of allowed) {
    const [w, h] = label.split(':').map(Number)
    if (!w || !h) continue
    const gap = Math.abs(w / h - target)
    if (gap < bestGap) {
      bestGap = gap
      best = label
    }
  }
  return best
}

/** The offered duration closest to the source clip's own runtime. */
function nearestDuration(
  seconds: number,
  allowed: number[],
  min: number,
  max: number,
): number {
  if (allowed.length) {
    return allowed.reduce((best, d) =>
      Math.abs(d - seconds) < Math.abs(best - seconds) ? d : best,
    )
  }
  return Math.min(max, Math.max(min, Math.round(seconds)))
}

/** Read a video file's intrinsic shape and runtime without uploading it. */
type VideoMeta = { width: number; height: number; duration: number }
function readVideoMeta(file: File): Promise<VideoMeta | null> {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file)
    const el = document.createElement('video')
    el.preload = 'metadata'
    const done = (value: VideoMeta | null) => {
      URL.revokeObjectURL(url)
      resolve(value)
    }
    el.onloadedmetadata = () =>
      done({
        width: el.videoWidth,
        height: el.videoHeight,
        duration: el.duration,
      })
    el.onerror = () => done(null)
    el.src = url
  })
}

/** Fired on the window when a generation finishes; see the dispatch site. */
export const FAL_TOOL_GENERATION_COMPLETE = 'fal-tool:generation-complete'

/**
 * Claim this page-session's single Meta `Lead`.
 *
 * The conversion moment is the generate click. It used to be ambiguous —
 * picking a file auth-gated straight into sign-up, so on an upload-first tool
 * that was the click that converted — but a signed-out visitor can now attach
 * a file and press generate, so there is one honest moment again.
 *
 * The claim survives because a page session can still reach generate more than
 * once (a plan gate, a failed run, a second clip). Sending `Lead` every time
 * would report a multiple of the real conversion rate and Meta would bid
 * against that inflated number, so the first caller gets the standard event
 * and every later one degrades to a custom event; PostHog and the Google tag
 * still receive the granular event name either way.
 */
function claimToolLead(slug: string): { metaStandardEvent?: string } {
  try {
    const key = `soku.tool_lead.v1:${slug}`
    if (window.sessionStorage.getItem(key)) return {}
    window.sessionStorage.setItem(key, '1')
  } catch {
    // A storage-less browser gets the un-deduplicated signal rather than none.
  }
  return { metaStandardEvent: 'Lead' }
}

/** One decimal is enough to show a stalled transfer moving. */
function formatMb(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1)
}

/**
 * Slack on the advertised source cap, matching `_SOURCE_SECONDS_TOLERANCE` on
 * the server. A clip an editor cut to "10 seconds" routinely reports 10.03,
 * and refusing that would be refusing the tool's own sample footage.
 */
const SOURCE_SECONDS_TOLERANCE = 0.5

type Phase = 'idle' | 'starting' | 'generating' | 'done' | 'error'
type UploadSlot = {
  name: string
  url: string
  /**
   * A local `blob:` preview for a file a signed-out visitor attached. It is
   * good enough to show and to read shot metadata from, but the generate
   * endpoint cannot fetch it — these are uploaded for real on the way back
   * from sign-in (see the rescue effect).
   */
  pending?: boolean
}

function acceptsUploadKind(file: File, kind: PendingUploadKind): boolean {
  return file.type.startsWith(`${kind}/`)
}

/**
 * Which ratio this tool opens on: the visitor's own choice if they have made
 * one, otherwise the SERVER's answer.
 *
 * The page used to start at a hardcoded '16:9'. Because that value is listed by
 * every ratio-taking tool, the "fall back to something valid" branch could
 * never fire, so `defaultAspectRatio` had no way to reach the control at all —
 * and the two reframe tools opened on landscape-to-landscape, the one setting
 * that changes nothing.
 */
function resolveAspect(spec: FalToolSpec, chosen: string | null): string {
  const preferred = chosen ?? spec.defaultAspectRatio
  if (!spec.aspectRatios.length) return preferred ?? DEFAULT_ASPECT_RATIOS[0]
  // A ratio the model does not list would fail after being charged for.
  return preferred && spec.aspectRatios.includes(preferred)
    ? preferred
    : spec.aspectRatios[0]
}

/**
 * Whether a document-level paste is this studio's to handle.
 *
 * A predicate rather than a condition inline in the effect, because it has now
 * been wrong twice in opposite directions and neither time was caught by
 * reading it. Too loose and it takes a screenshot pasted at the sidebar's
 * feedback popover and loads it as the tool's paid source asset; too tight and
 * the flow the feature exists for — open the page, press Cmd+V — does nothing,
 * because the drop zone is a <label> that cannot hold focus.
 *
 * The rule is "has something else CLAIMED this?", not "does the studio have
 * focus?". A paste with nothing focused reports the page itself as both its
 * target and its active element, and both readings have to exempt it — the
 * second regression was exempting only one of them.
 */
export function pasteBelongsToStudio({
  target,
  active,
  root,
  page,
}: {
  target: EventTarget | null
  active: Element | null
  root: { contains: (node: Node | null) => boolean } | null
  /** `[document.body, document.documentElement]` — the page, not a widget. */
  page: (Node | null)[]
}): boolean {
  if (!root) return false
  const isPageItself = (node: EventTarget | null) =>
    node === null || page.includes(node as Node)
  const claimedByAnother = (node: EventTarget | null) =>
    !isPageItself(node) && !root.contains(node as Node)
  return !claimedByAnother(target) && !claimedByAnother(active)
}

/** Whole megabytes, for a ceiling that is always a round number of them. */
function formatMbLimit(bytes: number): string {
  return `${Math.floor(bytes / (1024 * 1024))} MB`
}

/**
 * The first reason one of these files cannot be uploaded, in the visitor's
 * words — or null when they are all fine.
 *
 * Every check here also exists on the server, and has to: this one runs in the
 * same client that chose the file. It is here because the alternatives are all
 * worse to receive. Being told "this clip is 13.4s, trim it to 10s" before a
 * 40MB upload beats being told after it; being told a file is really a JPEG
 * beats paying for a render that dies at the model.
 */
async function firstUploadRejection(
  files: File[],
  kind: PendingUploadKind,
  spec: FalToolSpec,
): Promise<string | null> {
  for (const file of files) {
    if (spec.maxUploadBytes && file.size > spec.maxUploadBytes) {
      return `"${file.name}" exceeds the ${formatMbLimit(spec.maxUploadBytes)} limit — compress it and try again.`
    }
    // Bytes, not `file.type`: the browser derives the type from the extension,
    // so a JPEG renamed to .mp4 reports video/mp4 and walks straight past any
    // check written against it. Unknown signatures pass — see media-file-kind.
    const actual = await readMediaFileKind(file)
    if (actual && actual !== kind) {
      return `"${file.name}" is ${actual === 'image' ? 'an image' : `${actual === 'audio' ? 'an' : 'a'} ${actual}`} file, not ${kind === 'audio' ? 'an audio' : `a ${kind}`} file.`
    }
    if (kind === 'video' && spec.maxSourceSeconds) {
      const meta = await readVideoMeta(file)
      if (
        meta &&
        Number.isFinite(meta.duration) &&
        meta.duration > spec.maxSourceSeconds + SOURCE_SECONDS_TOLERANCE
      ) {
        return `"${file.name}" is ${meta.duration.toFixed(1)}s. This tool takes up to ${spec.maxSourceSeconds}s — trim it and try again.`
      }
    }
  }
  return null
}

interface HistoryItem {
  id: string
  /** Empty for text results, which carry `text` instead. */
  url: string
  text?: string
  kind: string
  prompt: string
  createdAt?: string
  /**
   * `preview` when this was the org's one free watermarked run. The stored
   * original is served at the same address once the plan allows video, so what
   * this changes is the upgrade banner and the canvas handoff, which has no
   * ledger row to authorize against. Carried by the server history too, so a
   * reload keeps both.
   */
  deliveryMode?: 'full' | 'preview'
  /**
   * What this output should be called when saved — source name plus target
   * ratio. Known only for a run this browser watched happen; rows restored
   * from the server fall back to the tool and the request id.
   */
  stem?: string
}

/**
 * The meaningful part of a saved output's filename.
 *
 * The typical use of these tools is one master through several placements, so
 * the ratio is the thing that has to be in the name — without it a folder of
 * downloads is a folder of identical-looking files.
 */
function downloadStem(opts: {
  sourceName?: string
  slug: string
  aspect: string | null
}): string {
  const base = opts.sourceName
    ? opts.sourceName.replace(/\.[^.]+$/, '')
    : opts.slug
  return [base, opts.aspect?.replace(':', 'x')].filter(Boolean).join('-')
}

function OutputPreview({
  url,
  text,
  kind,
  contain = false,
}: {
  url: string
  text?: string
  kind: string
  /** Dialog media keeps its intrinsic ratio instead of filling the dialog width. */
  contain?: boolean
}) {
  if (kind === 'text') {
    return (
      <div className="border-border bg-muted/30 text-foreground max-h-[28rem] overflow-y-auto rounded-xl border p-4 text-sm leading-relaxed whitespace-pre-wrap">
        {text}
      </div>
    )
  }
  if (kind === 'image') {
    // Not next/image: fal returns arbitrary CDN hosts that the loader is not
    // configured for, and these are one-off user outputs, not page assets.

    return (
      <img
        src={url}
        alt="Generated result"
        className={
          contain
            ? 'max-h-[70dvh] w-auto max-w-full rounded-xl object-contain'
            : 'w-full rounded-xl'
        }
      />
    )
  }
  if (kind === 'audio') {
    return <audio src={url} controls className="w-full" />
  }
  return (
    <video
      src={url}
      controls
      playsInline
      className={
        contain
          ? 'max-h-[70dvh] w-auto max-w-full rounded-xl object-contain'
          : 'w-full rounded-xl'
      }
    />
  )
}

/**
 * The thumbnail for one attached file, in the form.
 *
 * Every upload-first tool used to show its attachment as a filename and an X,
 * which is the one thing a visitor cannot check: on the translator the whole
 * job is "did I attach the right creative", and `01-cove.webp` does not answer
 * it. This is deliberately one component used by all three slots (image /
 * video / audio) rather than per-tool markup — the slots differ only in what a
 * preview MEANS, and audio has no frame to show, so it keeps the icon.
 */
function UploadThumb({ kind, url }: { kind: PendingUploadKind; url: string }) {
  const frame =
    'size-11 shrink-0 overflow-hidden rounded-md border border-border bg-muted/40'
  if (kind === 'image') {
    // Not next/image: fal-storage hosts are not in the loader's allowlist, and
    // these are throwaway inputs rather than page assets.

    return (
      <span className={frame}>
        <img src={url} alt="" className="size-full object-cover" />
      </span>
    )
  }
  if (kind === 'video') {
    // `#t=0.1` so the element paints a real frame instead of a black box —
    // same trick the canvas covers use.
    return (
      <span className={frame}>
        <video
          src={`${url}#t=0.1`}
          className="size-full object-cover"
          muted
          playsInline
          preload="metadata"
        />
      </span>
    )
  }
  return (
    <span className={`${frame} text-muted-foreground grid place-items-center`}>
      <Music className="size-4" />
    </span>
  )
}

export function FalToolStudio({
  slug,
  pagePath,
  title,
  promptPlaceholder = 'Describe what you want to create…',
  promptLabel,
  imageLabel,
  samplePrompts = [],
  brandId,
  lockedWorkspaceId,
  chrome = true,
  inlineHistory = true,
  canvasProjectId,
  idleSamples,
  showEngine = true,
}: {
  slug: string
  /** This page's own path — used for the auth round-trip and the Stripe return. */
  pagePath: string
  title: string
  promptPlaceholder?: string
  /**
   * Overrides the "Prompt" / "Script" heading over the text box, for tools
   * whose prompt is composed server-side. On the real-estate tool the listing
   * URL is the real input and the box is optional extra direction — calling it
   * "Prompt" tells a visitor they have to write one.
   */
  promptLabel?: string
  /** Same, for the upload slot: "Listing photos" beats "Reference images". */
  imageLabel?: string
  samplePrompts?: string[]
  /**
   * In-product mount only: the brand the result should land in. The marketing
   * pages omit it and let the server pick the visitor's acquisition brand.
   *
   * Note what this prop does NOT need to switch off. `ensureWorkspace` is
   * already gated on `organizations.count === 0` and the auth dialog on
   * `!isSignedIn`, so a signed-in user with a workspace skips both without any
   * variant flag — the only thing the two mounts genuinely disagree about is
   * where the canvas gets created.
   */
  brandId?: string
  /** Organization UUID fixed by an in-product `/o/...` route. */
  lockedWorkspaceId?: string
  /**
   * Whether to draw the card shell. Off when the studio is already inside a
   * dialog, which brings its own border, padding and background.
   */
  chrome?: boolean
  /**
   * The six-thumbnail strip under the result. On by default, because on a
   * marketing page this cramped strip is the ONLY history there is. The
   * in-product tool pages turn it off and render the same server-backed list
   * at full width with prompts and timestamps — two copies of one history on
   * one screen reads as a bug.
   */
  inlineHistory?: boolean
  /**
   * The canvas this page was opened FROM, when it was. With it, the handoff
   * adds the result to that canvas and returns there; without it, the result
   * seeds a new canvas as it always has. In-product only — a marketing
   * visitor has no canvas to have come from.
   */
  canvasProjectId?: string
  /**
   * Shown in the result column while nothing has been generated yet.
   *
   * That column is otherwise a dashed box reading "Your result appears here",
   * which is the least useful thing a first-time visitor can be shown: it asks
   * them to imagine the output instead of seeing one. Pages that have samples
   * hand them in here so the fold answers "what does this make" before the
   * visitor has spent anything.
   */
  idleSamples?: React.ReactNode
  /**
   * Whether to name the vendor model this tool runs on ("Runs on MiniMax H3").
   *
   * On a model landing page that name IS the product and belongs on screen.
   * On a paid-acquisition page it is a distraction at best — the visitor came
   * for the job, not the engine — and a shopping list at worst, since the one
   * thing a named model tells a cold visitor is which API to go buy directly.
   * The spec still owns the engine; this only decides whether to print it.
   */
  showEngine?: boolean
}) {
  const router = useRouter()
  const { isLoaded, bootstrapSettled, isSignedIn, getToken, user } =
    useAuthContext()
  const userId = user?.id ?? null
  const {
    activateWorkspace,
    organizations,
    selectedWorkspaceId,
    topUpReturn,
    upgradeReturn,
    workspaceSelectionRequired,
    workspaceSwitchError,
    workspaceSwitching,
  } = useToolWorkspaceOwner({ pagePath, lockedWorkspaceId })
  const {
    loadSpec,
    ensureWorkspace,
    uploadFile,
    generate,
    pollStatus,
    handoff,
    checkPreviewEligibility,
  } = useFalTool(slug)
  const openBilling = useBillingDialogStore(s => s.open)
  const planName = useCreditStore(s => s.planName)
  const planSlug = useCreditStore(s => s.planSlug)
  const isTopPlan = isTopPlanSlug(planSlug, planName)
  // AppSumo lifetime tiers have no plan above them: an empty balance is
  // recovered with a credit pack, never the subscription grid.
  const isAppSumo = isAppSumoPlan(planSlug)
  const { openTopUp, topUpDialog } = useToolCreditTopUp(topUpReturn)
  // False only once the balance has resolved and said so; null while unknown
  // and for logged-out visitors, who are told to sign up first either way.
  const toolGenerationAllowed = useCreditStore(s => s.toolGenerationAllowed)
  const generationMode = useCreditStore(s => s.toolGenerationMode)
  const setToolGenerationMode = useCreditStore(s => s.setToolGenerationMode)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const { failedRuns, rememberFailure } = useFailedToolRuns(slug)

  // Seeded from the page-load spec cache so switching back to a mode this
  // visitor already opened renders ready on the first frame instead of
  // flashing the loading skeleton. `useState` initialisers run during SSR too,
  // where the cache is always empty — so the server and the hydration render
  // still agree on `loading`.
  const cachedSpec = getCachedFalToolSpec(slug)
  const [spec, setSpec] = useState<FalToolSpec | null>(cachedSpec)
  // `preview` = the free-watermarked-preview experiment granted this org one
  // run: the button generates, the result carries a watermark. The grant is
  // org-wide but the server only honours it on video tools, so an image or
  // text tool in a preview-mode org is still simply blocked. Falls back to
  // the boolean for a balance payload predating the mode field.
  const previewMode =
    generationMode === 'preview' && spec?.outputKind === 'video'
  const planBlocked =
    generationMode === 'blocked' ||
    (generationMode === 'preview' && spec !== null && !previewMode) ||
    (generationMode === null && toolGenerationAllowed === false)

  // The experiment exposure: exactly once per page, and only when the
  // treatment could be shown — signed in, plan refuses generation, video
  // tool, workspace known. The server deals the arm here and nowhere else;
  // the balance poll only reports it afterwards.
  // Keyed by workspace, not a one-shot flag: switching workspaces switches the
  // org whose plan is being judged, and a stale answer must not be written
  // over the new one. The in-flight guard below discards a response whose
  // workspace is no longer the selected one — otherwise a control org could
  // be shown a free-preview button the server then refuses.
  const exposedWorkspaceRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (!isSignedIn || generationMode !== 'blocked') return
    if (spec?.outputKind !== 'video') return
    if (workspaceSelectionRequired || workspaceSwitching) return
    const workspaceId = lockedWorkspaceId ?? selectedWorkspaceId ?? null
    if (exposedWorkspaceRef.current === workspaceId) return
    exposedWorkspaceRef.current = workspaceId
    void checkPreviewEligibility(workspaceId).then(mode => {
      if (exposedWorkspaceRef.current !== workspaceId) return
      if (mode === 'preview') setToolGenerationMode('preview')
    })
  }, [
    checkPreviewEligibility,
    generationMode,
    isSignedIn,
    lockedWorkspaceId,
    selectedWorkspaceId,
    setToolGenerationMode,
    spec,
    workspaceSelectionRequired,
    workspaceSwitching,
  ])
  const [specState, setSpecState] = useState<'loading' | 'ready' | 'error'>(
    cachedSpec ? 'ready' : 'loading',
  )
  const [specAttempt, setSpecAttempt] = useState(0)
  const [prompt, setPrompt] = useState('')
  const [duration, setDuration] = useState<number | null>(null)
  const [resolution, setResolution] = useState<string | null>(null)
  // Null only until a spec is in hand; see `resolveAspect`. Seeded from the
  // page-load cache so switching modes does not blank the control for a frame.
  const [aspect, setAspect] = useState<string | null>(
    cachedSpec ? resolveAspect(cachedSpec, null) : null,
  )
  const [voice, setVoice] = useState<string | null>(null)
  const [language, setLanguage] = useState<string | null>(null)
  // Answers to whatever extra closed-enum controls the spec declares. Keyed by
  // the server's key, empty until the spec lands — the defaults are the
  // server's, so guessing one here would render a control that disagrees with
  // what an un-touched submit actually sends.
  const [choices, setChoices] = useState<Record<string, string>>({})
  const [sourceUrl, setSourceUrl] = useState('')
  const [images, setImages] = useState<UploadSlot[]>([])
  const [videos, setVideos] = useState<UploadSlot[]>([])
  const [audios, setAudios] = useState<UploadSlot[]>([])
  // Slot key -> setter, so an upload can target a slot without holding the
  // render-time `uploadSlots` array (the sign-in rescue runs from an effect).
  const slotSetters = useMemo(
    () => ({ image: setImages, video: setVideos, audio: setAudios }) as const,
    [],
  )
  // Files a signed-out visitor has attached but not uploaded, mirroring the
  // IndexedDB stash. The stash is replaced wholesale per (tool, slot), so a
  // second pick has to re-save the first one alongside it.
  const pendingFilesRef = useRef<Record<PendingUploadKind, File[]>>({
    image: [],
    video: [],
    audio: [],
  })
  const [uploading, setUploading] = useState(false)
  const [draggingSlot, setDraggingSlot] = useState<PendingUploadKind | null>(
    null,
  )
  // Bytes sent / bytes total for the file currently going up. A source ad is
  // routinely tens of megabytes, and a motionless "Uploading…" on a slow link
  // reads as a hang — at the exact step this page counts as its conversion.
  const [uploadProgress, setUploadProgress] = useState<[number, number] | null>(
    null,
  )
  // Attempt number / max attempts while a transient failure is being retried.
  // Non-null from the failed attempt until the next one moves its first byte.
  const [uploadRetry, setUploadRetry] = useState<[number, number] | null>(null)

  const [phase, setPhase] = useState<Phase>('idle')
  const [progressStage, setProgressStage] = useState('Submitting…')
  const [elapsed, setElapsed] = useState(0)
  const [history, setHistory] = useState<HistoryItem[]>([])
  /**
   * The output the Result column is showing, or null for none.
   *
   * Held whole, and deliberately NOT derived from `history`. Two things follow
   * from that. It is set only when a poll completes in THIS mount, so a reload
   * leaves the column empty (samples again) and the finished run stays where
   * it belongs, in the history strip — restoring it as if it had just finished
   * put an image in the result column of an idle form with no way to dismiss
   * it. And because it does not look itself up in `history`, the server-backed
   * history fetch replacing that list cannot make a result the visitor is
   * looking at vanish a few seconds after it arrived.
   */
  const [result, setResult] = useState<HistoryItem | null>(null)
  const [historyPreview, setHistoryPreview] = useState<HistoryItem | null>(null)
  const [authOpen, setAuthOpen] = useState(false)
  const [handoffBusy, setHandoffBusy] = useState(false)
  // The studio's own subtree. The paste listener has to be on the document
  // (the drop zone is a <label> and cannot hold focus), so this is what keeps
  // it from answering for the rest of the page.
  const rootRef = useRef<HTMLDivElement | null>(null)

  const pollAliveRef = useRef(false)
  const resumedRef = useRef(false)
  const hydratedRef = useRef(false)
  // Runs the poller completed in this mount; the server-history fetch merges
  // these instead of erasing them (its snapshot predates the completion).
  const locallyCompletedIdsRef = useRef<Set<string>>(new Set())

  // --- Spec ---------------------------------------------------------------
  useEffect(() => {
    let cancelled = false
    if (!getCachedFalToolSpec(slug)) setSpecState('loading')
    void loadSpec().then(s => {
      if (cancelled) return
      if (!s) {
        setSpecState('error')
        return
      }
      setSpec(s)
      setSpecState('ready')
      setDuration(d => d ?? s.defaultDuration ?? s.minDuration)
      setResolution(r => r ?? s.defaultResolution ?? s.resolutions[0] ?? null)
      setVoice(v => v ?? s.defaultVoice ?? s.voices[0] ?? null)
      setLanguage(l => l ?? s.defaultLanguage ?? s.languages[0] ?? null)
      setChoices(current => {
        const next = { ...current }
        for (const choice of s.choices ?? []) {
          next[choice.key] ??= choice.default
        }
        return next
      })
      // Order matters: adopt the spec's default only when nothing has chosen
      // yet, THEN clamp — a ratio the model does not list would fail after
      // being charged for.
      setAspect(a => resolveAspect(s, a))
    })
    return () => {
      cancelled = true
    }
  }, [loadSpec, slug, specAttempt])

  // --- Form persistence (survives the login reload + Stripe round-trip) ----
  // Browser-only, so it must stay in an effect rather than a lazy useState
  // initializer, which would run during SSR and cause a hydration mismatch.
  // It runs once after auth bootstrap confirms the identity that owns the data.
  useEffect(() => {
    if (!bootstrapSettled || hydratedRef.current) return
    try {
      const raw = sessionStorage.getItem(formKey(slug))
      if (raw) {
        const stored = parseFalToolPayload<Record<string, unknown>>(
          raw,
          userId,
          { allowAnonymousClaim: true },
        )
        if (stored.matches) {
          const f = stored.payload
          if (typeof f.prompt === 'string') setPrompt(f.prompt)
          if (typeof f.duration === 'number') setDuration(f.duration)
          if (typeof f.resolution === 'string') setResolution(f.resolution)
          if (typeof f.aspect === 'string') setAspect(f.aspect)
          if (typeof f.voice === 'string') setVoice(f.voice)
          if (typeof f.language === 'string') setLanguage(f.language)
          if (f.choices && typeof f.choices === 'object') {
            setChoices(f.choices as Record<string, string>)
          }
          if (typeof f.sourceUrl === 'string') setSourceUrl(f.sourceUrl)
          // Uploads are fal-hosted URLs, so they survive the round-trip as-is.
          if (Array.isArray(f.images)) setImages(f.images)
          if (Array.isArray(f.videos)) setVideos(f.videos)
          if (Array.isArray(f.audios)) setAudios(f.audios)
        } else {
          sessionStorage.removeItem(formKey(slug))
        }
      }
      const rawHistory = sessionStorage.getItem(historyKey(slug))
      if (rawHistory) {
        const stored = parseFalToolPayload<HistoryItem[]>(rawHistory, userId)
        if (stored.matches && Array.isArray(stored.payload)) {
          setHistory(stored.payload)
        } else {
          sessionStorage.removeItem(historyKey(slug))
        }
      }
    } catch {
      /* ignore malformed cache */
    }
    hydratedRef.current = true
  }, [bootstrapSettled, slug, userId])

  useEffect(() => {
    if (!hydratedRef.current) return
    try {
      sessionStorage.setItem(
        formKey(slug),
        JSON.stringify(
          stampFalToolPayload(
            {
              prompt,
              duration,
              resolution,
              aspect,
              voice,
              language,
              choices,
              sourceUrl,
              images,
              videos,
              audios,
            },
            userId,
          ),
        ),
      )
    } catch {
      /* ignore quota */
    }
  }, [
    slug,
    prompt,
    duration,
    resolution,
    aspect,
    voice,
    language,
    choices,
    sourceUrl,
    images,
    videos,
    audios,
    userId,
  ])

  useEffect(() => {
    if (!hydratedRef.current) return
    try {
      sessionStorage.setItem(
        historyKey(slug),
        JSON.stringify(stampFalToolPayload(history.slice(0, 12), userId)),
      )
    } catch {
      /* ignore quota */
    }
  }, [slug, history, userId])

  useEffect(() => {
    if (phase !== 'generating' && phase !== 'starting') return
    const t = setInterval(() => setElapsed(e => e + 1), 1000)
    return () => clearInterval(t)
  }, [phase])

  useEffect(() => {
    return () => {
      pollAliveRef.current = false
    }
  }, [])

  // Object URLs live until the document is discarded; a visitor who swaps
  // clips a few times on one page would otherwise pin every one of them.
  const attachedRef = useRef<UploadSlot[]>([])
  useEffect(() => {
    attachedRef.current = [...images, ...videos, ...audios]
  }, [images, videos, audios])
  useEffect(() => {
    return () => {
      for (const slot of attachedRef.current) {
        if (slot.pending) URL.revokeObjectURL(slot.url)
      }
    }
  }, [])

  const openAuth = useCallback(() => {
    router.replace(`${pagePath}?redirect_url=${encodeURIComponent(pagePath)}`, {
      scroll: false,
    })
    setAuthOpen(true)
  }, [router, pagePath])

  const cost = computeCreditCost(spec, {
    duration: duration ?? undefined,
    resolution: resolution ?? undefined,
  })

  // --- Uploads ------------------------------------------------------------
  /**
   * Point the shot controls at whatever the visitor just brought.
   *
   * Runs off the local `File`, before any upload, so it works identically for
   * a signed-in upload and for a signed-out attach — otherwise a vertical 13s
   * ad silently comes back landscape and 5s long once it has been paid for.
   */
  const applySourceShot = useCallback(
    async (picked: File[]) => {
      if (!spec) return
      if (!spec.followSourceAspectRatio && !spec.followSourceDuration) return
      if (!picked[0]?.type.startsWith('video/')) return
      const meta = await readVideoMeta(picked[0])
      if (!meta) return
      if (spec.followSourceAspectRatio) {
        const options = spec.aspectRatios.length
          ? spec.aspectRatios
          : DEFAULT_ASPECT_RATIOS
        const match = nearestAspectRatio(meta.width, meta.height, options)
        if (match) setAspect(match)
      }
      if (
        spec.followSourceDuration &&
        Number.isFinite(meta.duration) &&
        spec.minDuration != null &&
        spec.maxDuration != null
      ) {
        setDuration(
          nearestDuration(
            meta.duration,
            spec.durations,
            spec.minDuration,
            spec.maxDuration,
          ),
        )
      }
    },
    [spec],
  )

  /** Presign + PUT a set of already-vetted files, then append them to a slot. */
  const performUpload = useCallback(
    async (picked: File[], kind: PendingUploadKind, cap: number) => {
      if (!picked.length || !spec) return
      setErrorMsg(null)
      // Every path into an upload funnels here — the picker, the drop zone,
      // the paste handler and the post-sign-in rescue — so this is the one
      // place the checks have to live to cover all four.
      const rejection = await firstUploadRejection(picked, kind, spec)
      if (rejection) {
        setErrorMsg(rejection)
        return
      }
      setUploading(true)
      try {
        // Read the clip BEFORE the upload: on a tool that preserves the shot,
        // the controls have to reflect what the visitor just chose.
        await applySourceShot(picked)
        // Sequential, not `Promise.all`: progress from parallel transfers is
        // uninterpretable, and every tool here caps at a handful of files.
        const added: UploadSlot[] = []
        const totalBytes = picked.reduce((sum, f) => sum + f.size, 0)
        let doneBytes = 0
        for (const f of picked) {
          const url = await uploadFile(
            f,
            sent => {
              // The first byte of a retried attempt means the transfer is
              // moving again: show the bar rather than the retry notice.
              if (sent > 0) setUploadRetry(null)
              setUploadProgress([doneBytes + sent, totalBytes])
            },
            (attempt, maxAttempts) => setUploadRetry([attempt, maxAttempts]),
          )
          doneBytes += f.size
          setUploadRetry(null)
          setUploadProgress([doneBytes, totalBytes])
          added.push({ name: f.name, url })
        }
        // Functional append rather than a captured snapshot: the post-sign-in
        // rescue below runs outside render, so there is no `current` to spread.
        // Clamped, not just appended: two upload paths can both start before
        // the first setState commits (holding Cmd+V is the easy one) and both
        // read the same empty slot. Over-cap requests are rejected by the
        // server afterwards, with a message the visitor cannot act on.
        slotSetters[kind](prev => [...prev, ...added].slice(0, cap))
      } catch (error) {
        // Each cause has to read differently. Telling someone to "try a
        // different file" when their connection dropped sends them looking for
        // a problem that is not there — and saying it when the presign refused
        // a 300MB file sends them to change format when the answer was to
        // compress. The presign's only 4xx is the size ceiling. Every transient
        // cause has already been retried to exhaustion by the time it lands
        // here, so "try again" is asking for a new attempt, not a first one.
        const failure = error instanceof UploadFailure ? error : null
        setErrorMsg(
          failure?.message === 'upload_stalled' ||
            failure?.message === 'upload_network_error'
            ? 'Upload stalled — check your connection and try again.'
            : failure?.message === 'upload_presign_failed' &&
                failure.status === 422
              ? `That file is over the ${formatMbLimit(spec.maxUploadBytes)} limit — compress it and try again.`
              : 'Upload failed. Please try a different file.',
        )
      } finally {
        setUploading(false)
        setUploadProgress(null)
        setUploadRetry(null)
      }
    },
    [spec, uploadFile, slotSetters, applySourceShot],
  )

  /**
   * Drop one attached file. A signed-out attach lives in three places — the
   * slot, the object URL, and the IndexedDB stash — so removing it from only
   * the slot would leave the sign-in rescue re-uploading a file the visitor
   * had already taken back.
   */
  const removeSlotItem = useCallback(
    (kind: PendingUploadKind, index: number) => {
      const current = { image: images, video: videos, audio: audios }[kind]
      const removed = current[index]
      if (removed?.pending) {
        URL.revokeObjectURL(removed.url)
        // Positional, not by name: two files picked from different folders can
        // share one, and dropping both would silently take away an asset the
        // visitor kept.
        const pendingIndex = current
          .slice(0, index)
          .filter(slot => slot.pending).length
        const stashed = pendingFilesRef.current[kind].filter(
          (_, i) => i !== pendingIndex,
        )
        pendingFilesRef.current[kind] = stashed
        if (stashed.length) void savePendingUpload(slug, kind, stashed, null)
        else void clearPendingUpload(slug, kind)
      }
      slotSetters[kind](prev => prev.filter((_, i) => i !== index))
    },
    [images, videos, audios, slotSetters, slug],
  )

  const handleUpload = useCallback(
    async (
      files: FileList | File[] | null,
      kind: PendingUploadKind,
      current: UploadSlot[],
      cap: number,
    ) => {
      if (!files?.length || !spec) return
      const available = Math.max(0, cap - current.length)
      const dropped = Array.from(files)
      const picked = dropped
        .filter(file => acceptsUploadKind(file, kind))
        .slice(0, available)
      if (!picked.length) {
        setErrorMsg(
          available === 0
            ? `Up to ${cap} file${cap === 1 ? '' : 's'} for this tool.`
            : `Drop a${kind === 'audio' ? 'n' : ''} ${kind} file here.`,
        )
        return
      }
      if (!isSignedIn) {
        // Bringing a file is not the thing worth gating. Measured on the
        // 2026-08-28 Google campaign: 62 of 133 landing visitors hit the old
        // sign-in dialog here and only 5 ever reached the generate button, so
        // the wall was collecting the visitors instead of the intent. Signing
        // in now happens on generate, which is where the paid work starts.
        //
        // Check the file first either way: telling someone their clip is 13
        // seconds long only after they have signed up is the worst possible
        // order to deliver that in.
        const rejection = await firstUploadRejection(picked, kind, spec)
        if (rejection) {
          setErrorMsg(rejection)
          return
        }
        setErrorMsg(null)
        await applySourceShot(picked)
        // The presign endpoint is auth-gated, so the real upload has to wait
        // for the session. Show a local preview meanwhile and park the files
        // where a document reload cannot reach them — every way out of the
        // sign-in dialog reloads, which is what used to throw the pick away.
        // The stash is per (tool, slot) and replaced wholesale, so it has to
        // carry the files already attached in this visit as well.
        const stashed = [...pendingFilesRef.current[kind], ...picked].slice(
          0,
          cap,
        )
        pendingFilesRef.current[kind] = stashed
        await savePendingUpload(slug, kind, stashed, null)
        // Minted outside the updater: React may call that function twice, and
        // the second object URL would never be revoked.
        const previews: UploadSlot[] = picked.map(file => ({
          name: file.name,
          url: URL.createObjectURL(file),
          pending: true,
        }))
        slotSetters[kind](prev => [...prev, ...previews].slice(0, cap))
        track('fal_tool_asset_attached', {
          tool: slug,
          kind,
          signed_in: false,
        })
        return
      }
      await performUpload(picked, kind, cap)
    },
    [spec, slug, isSignedIn, performUpload, applySourceShot, slotSetters],
  )

  // The other half of that: on the way back from sign-in, upload whatever the
  // visitor had picked before it. Runs once per mount, and `takePendingUpload`
  // deletes as it reads, so a clip can never reattach to a later visit.
  const rescuedUploadsRef = useRef(false)
  useEffect(() => {
    if (!isLoaded || !bootstrapSettled || !isSignedIn || !spec) return
    if (rescuedUploadsRef.current) return
    rescuedUploadsRef.current = true
    const caps = {
      image: spec.maxImageUrls || 1,
      video: spec.maxVideoUrls || 1,
      audio: spec.maxAudioUrls || 1,
    }
    void (async () => {
      for (const kind of ['image', 'video', 'audio'] as const) {
        // Re-clamp on the way back in: the cap belongs to the spec, and the
        // stash was written before this mount could read it.
        const files = (await takePendingUpload(slug, kind, userId)).slice(
          0,
          caps[kind],
        )
        if (!files.length) continue
        // Drop the local previews first. Without this the same asset would
        // show twice — once as the `blob:` placeholder and once as the real
        // upload — and the second one would push the first over the cap.
        pendingFilesRef.current[kind] = []
        slotSetters[kind](prev => {
          for (const slot of prev) {
            if (slot.pending) URL.revokeObjectURL(slot.url)
          }
          return prev.filter(slot => !slot.pending)
        })
        await performUpload(files, kind, caps[kind])
      }
    })()
  }, [
    isLoaded,
    bootstrapSettled,
    isSignedIn,
    spec,
    slug,
    performUpload,
    slotSetters,
    userId,
  ])

  // Paste is the other half of drag-and-drop, and the one people reach for
  // when the asset came out of another app rather than off the disk: a frame
  // copied from a design tool, a clip copied from a file manager. Bound to the
  // document because the drop zone is a <label> and cannot hold focus.
  useEffect(() => {
    if (!spec) return
    const onPaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement | null
      // A paste aimed at the prompt box belongs to the prompt box.
      if (
        target &&
        (target.isContentEditable || /^(INPUT|TEXTAREA)$/.test(target.tagName))
      ) {
        return
      }
      // A paste aimed at another widget belongs to that widget. The in-product
      // mount sits inside a sidebar shell whose feedback popover listens for
      // pasted screenshots on purpose and holds focus on a button inside
      // itself, so a listener that only exempted text inputs would take that
      // screenshot and load it as the tool's paid source asset.
      //
      // "Another widget" means something actually HAS the paste, though — not
      // merely that this studio does not. On a freshly loaded page nothing is
      // focused, and the drop zone is a <label> that cannot take focus, so
      // requiring a focused descendant would break the one flow this feature
      // exists for: open the page, press Cmd+V.
      //
      if (
        !pasteBelongsToStudio({
          target,
          active: document.activeElement,
          root: rootRef.current,
          page: [document.body, document.documentElement],
        })
      ) {
        return
      }
      // A second paste before the first upload's state lands would read the
      // same empty slot and overshoot the cap.
      if (uploading) return
      const files = Array.from(event.clipboardData?.files ?? [])
      if (!files.length) return
      const slots = [
        { key: 'image' as const, items: images, cap: spec.maxImageUrls || 1 },
        { key: 'video' as const, items: videos, cap: spec.maxVideoUrls || 1 },
        { key: 'audio' as const, items: audios, cap: spec.maxAudioUrls || 1 },
      ].filter(slot => spec.inputs.includes(`${slot.key}_urls` as never))
      for (const slot of slots) {
        if (slot.items.length >= slot.cap) continue
        if (!files.some(file => acceptsUploadKind(file, slot.key))) continue
        event.preventDefault()
        void handleUpload(files, slot.key, slot.items, slot.cap)
        return
      }
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  }, [spec, images, videos, audios, uploading, handleUpload])

  // --- In-flight job persistence ------------------------------------------
  const clearPending = useCallback(() => {
    try {
      sessionStorage.removeItem(pendingKey(slug))
    } catch {
      /* private mode / storage disabled — the poll still works in-memory */
    }
  }, [slug])

  const markPending = useCallback(
    (requestId: string, promptUsed: string, stem: string) => {
      try {
        sessionStorage.setItem(
          pendingKey(slug),
          JSON.stringify(
            stampFalToolPayload(
              { requestId, prompt: promptUsed, stem },
              userId,
            ),
          ),
        )
      } catch {
        /* as above */
      }
    },
    [slug, userId],
  )

  // --- Poll ---------------------------------------------------------------
  const runPoll = useCallback(
    async (requestId: string, promptUsed: string, stem?: string) => {
      pollAliveRef.current = true
      let consecutiveFailures = 0
      // 20 minutes at 3s. The old 8-minute budget predates the model pages:
      // Veo 3.1 and Seedance 2.5 routinely run past it, and this very page
      // tells the visitor "generation usually takes 2-3 minutes" — a ceiling
      // that close to the expected runtime turns an ordinary slow render into
      // an apparent failure, on a clip the visitor has already paid for.
      const maxAttempts = 400
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (!pollAliveRef.current) return
        await new Promise(r => setTimeout(r, 3000))
        if (!pollAliveRef.current) return
        let res
        try {
          res = await pollStatus(requestId)
        } catch {
          res = null
        }
        // A single failed poll is noise; twenty in a row is not. Without this
        // the loop sat on a permanently-unreachable status for the full
        // budget, showing "Submitting…" the whole time — which is exactly what
        // a job stranded by a model-endpoint change looks like, and it read as
        // an app that had simply stopped.
        if (!res) {
          const recovery = recordStatusPollFailure(consecutiveFailures)
          consecutiveFailures = recovery.consecutiveFailures
          if (recovery.shouldPause) {
            setErrorMsg(
              "Lost contact with this job — we can't read its status yet. Come back later and we'll resume from the same paid job.",
            )
            setPhase('error')
            return
          }
          continue
        }
        consecutiveFailures = 0
        if (res.status === 'IN_QUEUE') {
          setProgressStage(
            res.queuePosition != null
              ? `In queue · position ${res.queuePosition}`
              : 'In queue…',
          )
        } else if (res.status === 'IN_PROGRESS') {
          setProgressStage('Rendering…')
        }
        if (res.status === 'COMPLETED' && (res.outputUrl || res.outputText)) {
          const finished: HistoryItem = {
            id: requestId,
            url: res.outputUrl ?? '',
            text: res.outputText ?? undefined,
            kind: res.outputKind ?? 'video',
            prompt: promptUsed,
            createdAt: new Date().toISOString(),
            stem,
            deliveryMode: res.deliveryMode === 'preview' ? 'preview' : 'full',
          }
          // Filtered, not just prepended. On a resume the server-backed history
          // fetch and this poll both land a row for the same request id, and
          // the mount-time guard below runs before that fetch resolves — so the
          // recovered run appeared twice and pushed the oldest one out of view.
          locallyCompletedIdsRef.current.add(requestId)
          setHistory(h => [
            finished,
            ...h.filter(item => item.id !== requestId),
          ])
          setResult(finished)
          setPhase('done')
          clearPending()
          track('fal_tool_generate_completed', {
            tool: slug,
            request_id: requestId,
            delivery_mode: finished.deliveryMode,
          })
          if (finished.deliveryMode === 'preview') {
            // The grant is spent: the next balance read flips the page to
            // the paywall for a second run while this result stays playable.
            void revalidateCreditBalance()
          }
          // Tell any sibling surface (the in-product tool page renders a full
          // run history next to this) that there is a new row to fetch. A DOM
          // event rather than lifted state: the studio is mounted by pages that
          // have no history panel at all, and the two have nothing else to say
          // to each other. Without it the first run of a session leaves the
          // history empty until a reload, which reads as "my result was lost".
          try {
            window.dispatchEvent(
              new CustomEvent(FAL_TOOL_GENERATION_COMPLETE, {
                detail: { slug },
              }),
            )
          } catch {
            /* CustomEvent is unavailable in non-browser test envs */
          }
          return
        }
        if (
          res.status === 'FAILED' ||
          res.status === 'ERROR' ||
          res.status === 'CANCELLED'
        ) {
          // The server refunds a job that fal accepted and then failed, so the
          // balance shown next is already corrected.
          void revalidateCreditBalance()
          setErrorMsg(
            toolFailureMessage(res.failure, res.deliveryMode, requestId),
          )
          rememberFailure(requestId, promptUsed, res.deliveryMode, res.failure)
          setPhase('error')
          clearPending()
          track('fal_tool_generate_failed', {
            tool: slug,
            request_id: requestId,
          })
          return
        }
      }
      // Out of budget, but the job is not lost: the pending marker stays, so
      // reopening this page resumes the poll. Saying "check back" is only
      // honest because of that — the previous copy promised the result would
      // "appear in history", which nothing was left running to do.
      setErrorMsg(
        'Still rendering — this one is taking a while. Leave this page open, or come back to it later and the result will be waiting.',
      )
      setPhase('error')
    },
    [clearPending, pollStatus, slug, rememberFailure],
  )

  // --- Permanent history ---------------------------------------------------
  //
  // Server-backed, so it survives closing the tab and the URLs never expire.
  // The sessionStorage copy above is kept purely as an instant first paint —
  // it is overwritten by this the moment the fetch lands.
  useEffect(() => {
    if (!isSignedIn) return
    let cancelled = false
    void (async () => {
      const token = await getBackendAuthToken(getToken, { skipCache: true })
      if (!token || cancelled) return
      const stored = await loadStoredGenerations(token, slug)
      if (cancelled) return
      setHistory(prev =>
        mergeServerHistory<HistoryItem>(
          stored.map(g => ({
            id: g.requestId,
            url: g.outputKind === 'text' ? '' : g.url,
            kind: g.outputKind,
            prompt: g.prompt ?? '',
            createdAt: g.createdAt,
            deliveryMode: historyDeliveryMode(g),
          })),
          prev,
          locallyCompletedIdsRef.current,
        ),
      )
    })()
    return () => {
      cancelled = true
    }
  }, [isSignedIn, getToken, slug])

  // A signed-out visitor has no runs to show.
  //
  // Sign-out hard-navigates, but sessionStorage survives it, so the hydration
  // effect above happily restored the previous session's list on the next
  // paint — a row of broken thumbnails under "Your previous runs", pointing at
  // result URLs this session can no longer fetch. Waiting for `bootstrapSettled`
  // matters: `isSignedIn` is false while the session is still being confirmed,
  // and clearing on that would wipe a returning visitor's own history.
  useEffect(() => {
    if (!isLoaded || !bootstrapSettled || isSignedIn) return
    setHistory([])
    setHistoryPreview(null)
    try {
      sessionStorage.removeItem(historyKey(slug))
    } catch {
      /* ignore */
    }
  }, [isLoaded, bootstrapSettled, isSignedIn, slug])

  // --- Resume an in-flight job after a reload ------------------------------
  //
  // Without this, refreshing during a render silently threw away a paid job:
  // the credits were already deducted and fal was already working, but the only
  // record of the request id was React state. The visitor came back to an empty
  // studio and no way to reach the clip they bought.
  //
  // Runs once, after the spec resolves — the poller writes into `history`,
  // which the hydration effect above has to have populated first or a resumed
  // result would land on top of a not-yet-restored list.
  useEffect(() => {
    if (!bootstrapSettled || !spec || resumedRef.current) return
    resumedRef.current = true
    type PendingJob = {
      requestId?: string
      prompt?: string
      stem?: string
    }
    let pending: PendingJob | null = null
    try {
      const raw = sessionStorage.getItem(pendingKey(slug))
      if (raw) {
        const stored = parseFalToolPayload<PendingJob>(raw, userId)
        if (stored.matches) {
          pending = stored.payload
        } else {
          sessionStorage.removeItem(pendingKey(slug))
        }
      }
    } catch {
      pending = null
    }
    if (!pending?.requestId) return
    // Already finished and recorded before the reload — nothing to wait for.
    if (history.some(h => h.id === pending.requestId)) {
      clearPending()
      return
    }
    track('fal_tool_generate_resumed', {
      tool: slug,
      request_id: pending.requestId,
    })
    setPhase('generating')
    setProgressStage('Picking up where you left off…')
    void runPoll(pending.requestId, pending.prompt ?? '', pending.stem)
    // `history` is intentionally not a dependency: this must fire once on
    // mount, and re-running it as history changes would restart the poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapSettled, spec, slug, userId, clearPending, runPoll])

  // --- Generate -----------------------------------------------------------
  const handleGenerate = useCallback(async () => {
    if (!spec) return
    track(
      'fal_tool_generate_clicked',
      {
        tool: slug,
        signed_in: isSignedIn,
        cost,
      },
      // The paid-traffic conversion for every tool landing page: the visitor
      // brought an asset, configured a run, and asked for it. Without a
      // standard event Meta only sees `trackCustom` and will not bid on it
      // until someone hand-builds a custom conversion.
      claimToolLead(slug),
    )

    if (!isSignedIn) {
      openAuth()
      return
    }

    // Signed in with a local preview still attached: the rescue upload is
    // either in flight or was interrupted. Submitting now would send the
    // generate endpoint a `blob:` URL it cannot fetch.
    if ([...images, ...videos, ...audios].some(slot => slot.pending)) {
      setErrorMsg('Still uploading your file — try again in a moment.')
      return
    }

    // The server would reject this with 402 fal_video_plan_required anyway;
    // going straight to the plans skips a round trip whose only content is bad
    // news, and skips burning the visitor's prompt on it.
    //
    // `plan_gate_blocked`, not `upgrade_clicked`: nobody asked to upgrade here.
    // The visitor asked to generate and the plan refused, and a dashboard that
    // reads this as intent to buy reports a wish where there was a wall.
    if (planBlocked) {
      track('fal_tool_plan_gate_blocked', {
        tool: slug,
        stage: 'pre_submit',
        reason: 'video_requires_creator',
      })
      openBilling('upgrade', upgradeReturn)
      return
    }

    setErrorMsg(null)
    setElapsed(0)
    setProgressStage('Submitting…')
    setPhase('starting')
    const promptUsed = prompt.trim()
    try {
      if (
        organizations.isLoaded &&
        shouldEnsureToolWorkspace({
          workspaceCount: organizations.count,
          lockedWorkspaceId,
        })
      ) {
        await ensureWorkspace()
      }
      const params: GenerateParams = {}
      params.workspaceId = lockedWorkspaceId ?? selectedWorkspaceId ?? undefined
      if (spec.inputs.includes('prompt')) params.prompt = promptUsed
      if (spec.inputs.includes('duration') && duration != null)
        params.duration = duration
      if (spec.inputs.includes('resolution') && resolution)
        params.resolution = resolution
      if (spec.inputs.includes('aspect_ratio') && aspect)
        params.aspectRatio = aspect
      if (spec.inputs.includes('voice') && voice) params.voice = voice
      if (spec.inputs.includes('language') && language)
        params.language = language
      if (spec.choices?.length) {
        // Only the keys this spec declares: the server rejects an unknown one
        // rather than dropping it, which is what keeps the page and the job
        // describing the same request.
        params.choices = Object.fromEntries(
          spec.choices.map(choice => [
            choice.key,
            choices[choice.key] ?? choice.default,
          ]),
        )
      }
      if (spec.inputs.includes('source_url') && sourceUrl.trim())
        params.sourceUrl = sourceUrl.trim()
      if (spec.inputs.includes('image_urls'))
        params.imageUrls = images.map(i => i.url)
      if (spec.inputs.includes('video_urls'))
        params.videoUrls = videos.map(v => v.url)
      if (spec.inputs.includes('audio_urls'))
        params.audioUrls = audios.map(a => a.url)

      const result = await generate(params)

      if (result.status === 'unauthenticated') {
        openAuth()
        setPhase('idle')
        return
      }
      if (result.status === 'invalid') {
        setErrorMsg(result.message)
        setPhase('idle')
        return
      }
      if (result.status === 'insufficient') {
        track('fal_tool_insufficient_credits', {
          tool: slug,
          needed: result.needed ?? cost,
          balance: result.balance,
          top_plan: isTopPlan,
        })
        void revalidateCreditBalance()
        if (isAppSumo) {
          openTopUp()
          setPhase('idle')
          return
        }
        if (isTopPlan) {
          setErrorMsg(
            "You're out of credits for this cycle — they refresh on your renewal date.",
          )
          setPhase('idle')
          return
        }
        track('fal_tool_plan_gate_blocked', {
          tool: slug,
          stage: 'pre_submit',
          reason: 'out_of_credits',
        })
        openBilling('exhausted', upgradeReturn)
        setPhase('idle')
        return
      }
      if (result.status === 'plan_required') {
        track('fal_tool_plan_gate_blocked', {
          tool: slug,
          stage: 'server_402',
          reason: 'video_requires_creator',
        })
        openBilling('upgrade', upgradeReturn)
        setPhase('idle')
        return
      }
      if (result.status === 'workspace_selection_required') {
        setErrorMsg('Choose a workspace before generating.')
        setPhase('idle')
        return
      }
      if (result.status === 'error') {
        setErrorMsg(result.message)
        setPhase('error')
        return
      }
      track('fal_tool_generate_started', {
        tool: slug,
        request_id: result.requestId,
        credits_charged: result.creditsCharged,
        delivery_mode: previewMode ? 'preview' : 'full',
      })
      void revalidateCreditBalance()
      setPhase('generating')
      const stem = downloadStem({
        sourceName: [...videos, ...images, ...audios][0]?.name,
        slug,
        // Only for a tool that HAS a ratio control. `aspect` resolves to a
        // value for every tool, so an upscaler's vertical output would
        // otherwise be labelled 16:9.
        aspect: spec.inputs.includes('aspect_ratio') ? aspect : null,
      })
      markPending(result.requestId, promptUsed, stem)
      void runPoll(result.requestId, promptUsed, stem)
    } catch {
      setErrorMsg('Something went wrong. Please try again.')
      setPhase('error')
    }
  }, [
    spec,
    slug,
    isSignedIn,
    isTopPlan,
    isAppSumo,
    openTopUp,
    planBlocked,
    previewMode,
    cost,
    prompt,
    duration,
    resolution,
    aspect,
    voice,
    language,
    choices,
    sourceUrl,
    images,
    videos,
    audios,
    organizations.isLoaded,
    organizations.count,
    selectedWorkspaceId,
    lockedWorkspaceId,
    ensureWorkspace,
    markPending,
    generate,
    openAuth,
    openBilling,
    upgradeReturn,
    runPoll,
  ])

  // --- Hand off into the product -----------------------------------------
  // The point of the whole flow: the visitor already has a real org and brand,
  // so the result can open as a project in Creatives instead of dead-ending here.
  const handleOpenInProduct = useCallback(
    async (item: HistoryItem) => {
      setHandoffBusy(true)
      track('fal_tool_handoff_clicked', {
        tool: slug,
        request_id: item.id,
        ...(canvasProjectId ? { target: 'existing_canvas' } : {}),
      })
      const result = await handoff(
        item.id,
        item.prompt,
        brandId,
        canvasProjectId,
      )
      if (!result) {
        setErrorMsg(
          canvasProjectId
            ? 'Could not add this to the canvas. Please try again.'
            : 'Could not open this in the product. Please try again.',
        )
        setHandoffBusy(false)
        return
      }
      router.push(result.path)
    },
    [brandId, canvasProjectId, handoff, router, slug],
  )

  /**
   * The first unmet `required` field, or null when the form is submittable.
   *
   * The button used to be gated on `busy || uploading` only, so a tool with a
   * required prompt or a required upload let you click Generate with it empty
   * and answered with a 400. That reads as a broken product rather than a
   * missing field — especially on the localization tool, whose prompt
   * placeholder is a plausible-looking value ("Japanese") that people
   * reasonably assume is already filled in.
   *
   * Driven by the SERVER's `required` list, so a newly registered tool is
   * gated correctly without touching this file.
   */
  const missingRequired = useMemo(() => {
    if (!spec) return null
    // An OR across inputs, checked before the AND below. The real-estate tool
    // needs a listing URL *or* photos, which `required` cannot express — and
    // gating the button on either alone would refuse a legitimate run.
    if (spec.requireAny?.length) {
      const present: Record<string, boolean> = {
        prompt: !!prompt.trim(),
        source_url: !!sourceUrl.trim(),
        image_urls: images.length > 0,
        video_urls: videos.length > 0,
        audio_urls: audios.length > 0,
      }
      if (!spec.requireAny.some(f => present[f])) {
        return spec.requireAnyMessage || 'Add a source first'
      }
    }
    for (const field of spec.required) {
      switch (field) {
        case 'prompt':
          if (!prompt.trim()) return 'Describe what you want first'
          break
        case 'language':
          if (!language) return 'Pick a target language'
          break
        case 'voice':
          if (!voice) return 'Pick a voice'
          break
        case 'source_url':
          if (!sourceUrl.trim())
            return `Paste a ${spec.sourceUrlLabel.toLowerCase()}`
          break
        case 'image_urls':
          if (images.length < Math.max(1, spec.minImageUrls)) {
            return spec.minImageUrls > 1
              ? `Upload ${spec.minImageUrls} images first`
              : 'Upload an image first'
          }
          break
        case 'video_urls':
          if (!videos.length) return 'Upload a video first'
          break
        case 'audio_urls':
          if (!audios.length) return 'Upload an audio track first'
          break
        default:
          break
      }
    }
    return null
  }, [spec, prompt, language, voice, sourceUrl, images, videos, audios])

  const busy = phase === 'starting' || phase === 'generating'
  const latest = result

  const generateLabel = busy
    ? phase === 'starting'
      ? 'Starting…'
      : 'Generating…'
    : !isSignedIn
      ? 'Sign up to Generate'
      : planBlocked
        ? // Name the actual next step. "Generate" here was a button whose only
          // possible outcome was a 402 — after the visitor had written a prompt
          // and, on the edit tools, uploaded a file.
          'Upgrade to generate'
        : previewMode
          ? 'Generate free preview'
          : 'Generate with Soku AI'

  if (!spec) {
    if (specState === 'error') {
      return (
        <div className="border-border bg-card flex w-full flex-col items-center justify-center rounded-2xl border px-6 py-12 text-center">
          <AlertCircle className="size-6 text-amber-600" />
          <p className="text-foreground mt-3 text-sm font-semibold">
            The local tool API is unavailable
          </p>
          <p className="text-muted-foreground mt-1 max-w-md text-sm leading-6">
            Start the API and its Redis/Postgres dependencies, then retry. Your
            page and sample gallery are still available below.
          </p>
          <Button
            type="button"
            variant="outline"
            className="mt-5 rounded-full"
            onClick={() => setSpecAttempt(attempt => attempt + 1)}
          >
            <RefreshCw className="size-4" /> Retry
          </Button>
        </div>
      )
    }
    return (
      <div className="border-border bg-card flex w-full items-center justify-center rounded-2xl border p-12">
        <Loader2 className="text-muted-foreground size-5 animate-spin" />
      </div>
    )
  }

  const uploadSlots = [
    {
      key: 'image' as const,
      show: spec.inputs.includes('image_urls'),
      label:
        imageLabel ?? (spec.maxImageUrls > 1 ? 'Reference images' : 'Image'),
      accept: 'image/*',
      items: images,
      setter: setImages,
      cap: spec.maxImageUrls || 1,
    },
    {
      key: 'video' as const,
      show: spec.inputs.includes('video_urls'),
      label: spec.maxVideoUrls > 1 ? 'Reference clips' : 'Video',
      accept: 'video/*',
      items: videos,
      setter: setVideos,
      cap: spec.maxVideoUrls || 1,
    },
    {
      key: 'audio' as const,
      show: spec.inputs.includes('audio_urls'),
      label: 'Audio',
      accept: 'audio/*',
      items: audios,
      setter: setAudios,
      cap: spec.maxAudioUrls || 1,
    },
  ].filter(s => s.show)

  return (
    <div
      ref={rootRef}
      className={
        chrome
          ? 'border-border bg-card w-full rounded-2xl border p-4 shadow-sm sm:p-8'
          : 'w-full'
      }
    >
      <PricingDialog variant={pricingDialogVariantForPagePath(pagePath)} />
      <FalToolAuthDialog
        open={authOpen}
        onOpenChange={setAuthOpen}
        toolTitle={title}
      />
      <Dialog
        open={historyPreview !== null}
        onOpenChange={open => {
          if (!open) setHistoryPreview(null)
        }}
      >
        <DialogContent className="w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] border-white/10 bg-stone-950 p-4 text-white sm:max-w-5xl sm:p-6">
          <div className="min-w-0 pr-10">
            <DialogTitle className="font-serif text-xl">
              Generation result
            </DialogTitle>
            <DialogDescription className="mt-1 line-clamp-2 text-white/50">
              {historyPreview?.prompt || 'Generated with Soku AI'}
            </DialogDescription>
          </div>
          {historyPreview ? (
            <div className="flex max-h-[70dvh] min-h-48 items-center justify-center overflow-auto rounded-xl bg-black/40 p-2 sm:p-4">
              <OutputPreview
                url={assetMediaSrc(historyPreview.url) ?? historyPreview.url}
                text={historyPreview.text}
                kind={historyPreview.kind}
                contain
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
        {showEngine ? (
          <p className="text-muted-foreground text-[10px] tracking-[0.3em] uppercase">
            Runs on {spec.engine}
          </p>
        ) : null}
        {spec.notes && (
          <p className="text-muted-foreground text-xs">{spec.notes}</p>
        )}
      </div>

      {isSignedIn && organizations.isLoaded ? (
        <ToolWorkspacePicker
          workspaces={organizations.data}
          selectedWorkspaceId={selectedWorkspaceId}
          lockedWorkspaceId={lockedWorkspaceId}
          disabled={workspaceSwitching}
          onSelect={activateWorkspace}
        />
      ) : null}

      {/* minmax(0, …) + min-w-0: an fr track's floor is its content width, and the
          sample-prompt chips are single-line, so without this the widest chip set
          the whole column and the result rail collapsed beside it. */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
        {/* Controls */}
        <div className="min-w-0 space-y-5">
          {/* Above the prompt on purpose: on the tools that have one, this is
              the input the visitor arrived holding — a listing they already
              published — and everything below it is optional refinement. */}
          {spec.inputs.includes('source_url') && (
            <div>
              <label
                className="text-muted-foreground text-xs font-medium"
                htmlFor="fal-source-url"
              >
                {spec.sourceUrlLabel}
                {spec.required.includes('source_url') && ' (required)'}
              </label>
              <Input
                id="fal-source-url"
                type="url"
                inputMode="url"
                value={sourceUrl}
                onChange={e => setSourceUrl(e.target.value)}
                placeholder={spec.sourceUrlPlaceholder}
                className="mt-2"
                maxLength={2048}
              />
            </div>
          )}

          {spec.inputs.includes('prompt') && (
            <div>
              <label
                className="text-muted-foreground text-xs font-medium"
                htmlFor="fal-prompt"
              >
                {promptLabel ??
                  (spec.outputKind === 'audio' ? 'Script' : 'Prompt')}
              </label>
              <Textarea
                id="fal-prompt"
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder={promptPlaceholder}
                rows={4}
                className="mt-2 resize-none"
                maxLength={2000}
              />
              {samplePrompts.length > 0 && !prompt && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {samplePrompts.map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setPrompt(s)}
                      title={s}
                      className="border-border text-muted-foreground hover:bg-muted max-w-full truncate rounded-full border px-3 py-1 text-xs"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {uploadSlots.map(slot => (
            <div key={slot.key}>
              <label className="text-muted-foreground text-xs font-medium">
                {slot.label}
                {spec.required.includes(`${slot.key}_urls` as never) &&
                  ' (required)'}
                {slot.key !== 'image' && spec.maxSourceSeconds
                  ? ` · up to ${spec.maxSourceSeconds}s`
                  : ''}
              </label>
              <div className="mt-2 space-y-2">
                {slot.items.map((f, i) => (
                  <div
                    key={f.url}
                    className="border-border flex items-center gap-3 rounded-lg border p-2 text-xs"
                  >
                    <UploadThumb kind={slot.key} url={f.url} />
                    <span className="text-muted-foreground min-w-0 flex-1 truncate">
                      {f.name}
                    </span>
                    <button
                      type="button"
                      aria-label={`Remove ${f.name}`}
                      className="shrink-0 px-1"
                      onClick={() => removeSlotItem(slot.key, i)}
                    >
                      <X className="size-3.5 opacity-60 hover:opacity-100" />
                    </button>
                  </div>
                ))}
                {slot.items.length < slot.cap && (
                  <label
                    /* A drop target has to look like somewhere a file can be
                       dropped. At one line tall it read as a button, and it
                       was the shortest thing on a form whose whole first step
                       is "bring your creative". Fixed height rather than
                       padding so image, video and audio slots — and a tool
                       with two of them — present the same target. */
                    className={`text-muted-foreground flex h-32 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-3 text-xs transition-colors ${
                      draggingSlot === slot.key
                        ? 'border-orange-500 bg-orange-500/10 text-orange-600'
                        : 'border-border hover:bg-muted/50'
                    }`}
                    onDragEnter={event => {
                      event.preventDefault()
                      setDraggingSlot(slot.key)
                    }}
                    onDragOver={event => {
                      event.preventDefault()
                      event.dataTransfer.dropEffect = 'copy'
                    }}
                    onDragLeave={event => {
                      if (
                        !event.currentTarget.contains(
                          event.relatedTarget as Node,
                        )
                      ) {
                        setDraggingSlot(current =>
                          current === slot.key ? null : current,
                        )
                      }
                    }}
                    onDrop={event => {
                      event.preventDefault()
                      setDraggingSlot(null)
                      void handleUpload(
                        Array.from(event.dataTransfer.files),
                        slot.key,
                        slot.items,
                        slot.cap,
                      )
                    }}
                  >
                    {uploading ? (
                      <Loader2 className="size-5 animate-spin" />
                    ) : (
                      <Upload className="size-5" />
                    )}
                    <span className="text-center">
                      {uploading
                        ? uploadRetry
                          ? `Upload interrupted — retrying (${uploadRetry[0]}/${uploadRetry[1]})…`
                          : uploadProgress
                            ? `Uploading… ${Math.round((uploadProgress[0] / uploadProgress[1]) * 100)}% · ${formatMb(uploadProgress[0])} / ${formatMb(uploadProgress[1])} MB`
                            : 'Uploading…'
                        : draggingSlot === slot.key
                          ? `Drop ${slot.key} to upload`
                          : `Upload or drop ${slot.key}`}
                    </span>
                    {!uploading && draggingSlot !== slot.key && (
                      <span className="text-muted-foreground/60">
                        {slot.cap > 1 ? `Up to ${slot.cap} files` : 'One file'}
                        {slot.key !== 'image' && spec.maxSourceSeconds
                          ? ` · up to ${spec.maxSourceSeconds}s`
                          : ''}
                        {spec.maxUploadBytes
                          ? ` · max ${formatMbLimit(spec.maxUploadBytes)}`
                          : ''}
                      </span>
                    )}
                    <input
                      type="file"
                      accept={slot.accept}
                      multiple={slot.cap > 1}
                      className="hidden"
                      onChange={e => {
                        void handleUpload(
                          e.target.files,
                          slot.key,
                          slot.items,
                          slot.cap,
                        )
                        e.target.value = ''
                      }}
                    />
                  </label>
                )}
              </div>
            </div>
          ))}

          <div className="grid grid-cols-2 gap-3">
            {spec.inputs.includes('duration') && spec.minDuration != null && (
              <div>
                <label className="text-muted-foreground text-xs font-medium">
                  Duration
                </label>
                <Select
                  value={String(
                    duration ?? spec.defaultDuration ?? spec.minDuration,
                  )}
                  onValueChange={v => setDuration(Number(v))}
                >
                  <SelectTrigger className="mt-2 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* `durations` wins when the model is sparse; the contiguous
                        range stays the default so no existing tool changes. */}
                    {(spec.durations.length
                      ? spec.durations
                      : Array.from(
                          {
                            length:
                              (spec.maxDuration ?? 5) - spec.minDuration + 1,
                          },
                          (_, i) => spec.minDuration! + i,
                        )
                    ).map(d => (
                      <SelectItem key={d} value={String(d)}>
                        {d}s
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {spec.inputs.includes('resolution') &&
              spec.resolutions.length > 0 && (
                <div>
                  <label className="text-muted-foreground text-xs font-medium">
                    Resolution
                  </label>
                  <Select
                    value={resolution ?? ''}
                    onValueChange={setResolution}
                  >
                    <SelectTrigger className="mt-2 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {spec.resolutions.map(r => (
                        <SelectItem key={r} value={r}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

            {spec.inputs.includes('aspect_ratio') && (
              <div>
                <label className="text-muted-foreground text-xs font-medium">
                  Aspect ratio
                </label>
                <Select value={aspect ?? ''} onValueChange={setAspect}>
                  <SelectTrigger className="mt-2 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(spec.aspectRatios.length
                      ? spec.aspectRatios
                      : DEFAULT_ASPECT_RATIOS
                    ).map(r => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {spec.inputs.includes('language') && spec.languages.length > 0 && (
              <div>
                <label className="text-muted-foreground text-xs font-medium">
                  {spec.languageLabel || 'Target language'}
                </label>
                <Select value={language ?? ''} onValueChange={setLanguage}>
                  <SelectTrigger className="mt-2 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {spec.languages.map(l => (
                      <SelectItem key={l} value={l}>
                        {l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {(spec.choices ?? []).map(choice => (
              <div key={choice.key}>
                <label className="text-muted-foreground text-xs font-medium">
                  {choice.label}
                </label>
                <Select
                  value={choices[choice.key] ?? choice.default}
                  onValueChange={value =>
                    setChoices(current => ({ ...current, [choice.key]: value }))
                  }
                >
                  <SelectTrigger className="mt-2 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {choice.options.map(option => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}

            {spec.inputs.includes('voice') && spec.voices.length > 0 && (
              <div>
                <label className="text-muted-foreground text-xs font-medium">
                  Voice
                </label>
                <Select value={voice ?? ''} onValueChange={setVoice}>
                  <SelectTrigger className="mt-2 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {spec.voices.map(v => (
                      <SelectItem key={v} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {topUpDialog}

          {isSignedIn && (
            <CreditBadge
              cost={cost}
              planBlocked={planBlocked}
              previewMode={previewMode}
              onBuyCredits={openTopUp}
              onUpgrade={() => {
                track('fal_tool_upgrade_clicked', {
                  tool: slug,
                  source: 'credit_badge',
                })
                openBilling('upgrade', upgradeReturn)
              }}
            />
          )}

          {(workspaceSwitchError ?? errorMsg) ? (
            <p role="alert" className="text-xs break-words text-amber-600">
              {workspaceSwitchError ?? errorMsg}
            </p>
          ) : null}

          <Button
            className="w-full rounded-full"
            size="lg"
            // Once the button is an upgrade CTA, form completeness is
            // irrelevant. The old condition renamed it to Upgrade and then
            // disabled it until a prompt/file was supplied, leaving See plans
            // as the only working route.
            disabled={
              busy ||
              uploading ||
              workspaceSwitching ||
              workspaceSelectionRequired ||
              (!planBlocked && missingRequired !== null)
            }
            onClick={() => void handleGenerate()}
          >
            {busy ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Zap className="size-4" />
            )}
            {generateLabel}
          </Button>
          {missingRequired && !busy && (
            <p className="text-muted-foreground mt-2 text-center text-xs">
              {missingRequired}
            </p>
          )}
          {!isSignedIn && (
            <p className="text-muted-foreground text-center text-xs">
              Sign up on this page — generating runs on a paid plan, from
              $19/mo.
            </p>
          )}
        </div>

        {/* Output */}
        <div className="min-w-0 space-y-4">
          <p className="text-muted-foreground text-[10px] tracking-[0.3em] uppercase">
            Result
          </p>

          {busy && (
            <div className="border-border bg-muted/30 flex h-64 flex-col items-center justify-center gap-3 rounded-xl border">
              <Loader2 className="text-muted-foreground size-6 animate-spin" />
              <p className="text-muted-foreground text-sm">{progressStage}</p>
              <p className="text-muted-foreground text-xs">
                {elapsed}s elapsed
              </p>
              <p className="text-muted-foreground/70 max-w-xs px-4 text-center text-xs">
                This may take a few minutes. Keep this page open or come back
                later — your result is saved automatically.
              </p>
            </div>
          )}

          {!busy && latest && (
            <div className="space-y-3">
              <OutputPreview
                url={assetMediaSrc(latest.url) ?? latest.url}
                text={latest.text}
                kind={latest.kind}
              />
              {latest.deliveryMode === 'preview' && (
                <FreePreviewResultBanner
                  testId="fal-tool-preview-banner"
                  onUpgrade={() => {
                    track('fal_tool_preview_upgrade_clicked', {
                      tool: slug,
                      request_id: latest.id,
                    })
                    openBilling('upgrade', upgradeReturn)
                  }}
                />
              )}
              <div className="flex flex-wrap gap-2">
                {latest.kind === 'text' ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={() =>
                      void navigator.clipboard.writeText(latest.text ?? '')
                    }
                  >
                    <Copy className="size-4" />
                    Copy
                  </Button>
                ) : (
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                  >
                    <a
                      href={
                        assetDownloadSrc(
                          latest.url,
                          latest.stem ?? `${slug}-${latest.id.slice(0, 8)}`,
                        ) ?? latest.url
                      }
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Download className="size-4" />
                      Download
                    </a>
                  </Button>
                )}
                <Button
                  size="sm"
                  className="rounded-full"
                  disabled={
                    handoffBusy ||
                    latest.kind === 'text' ||
                    // A preview has no ledger row for the handoff to
                    // authorize against, and a canvas must not receive the
                    // unwatermarked original anyway.
                    latest.deliveryMode === 'preview'
                  }
                  onClick={() => void handleOpenInProduct(latest)}
                >
                  {handoffBusy ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <ArrowRight className="size-4" />
                  )}
                  {canvasProjectId
                    ? 'Add to this canvas'
                    : brandId
                      ? 'Open on a canvas'
                      : 'Open in Soku Creatives'}
                </Button>
              </div>
              {/* In-product this block would be telling users about the room
                  they are standing in, so it is marketing-mount only. */}
              {canvasProjectId ? (
                <p className="text-muted-foreground text-xs">
                  Adds this result as a node on the canvas you came from, next
                  to what is already there.
                </p>
              ) : brandId ? (
                <p className="text-muted-foreground text-xs">
                  Opens this result as a canvas node — edit it, branch variants
                  off it, or assemble it into an ad.
                </p>
              ) : (
                <div className="border-border bg-muted/30 rounded-xl border p-4">
                  <p className="text-foreground text-xs font-medium">
                    {showEngine ? `This ran on ${spec.engine}. ` : ''}On a Soku
                    canvas the same{' '}
                    {latest.kind === 'text' ? 'draft' : latest.kind} can:
                  </p>
                  <ul className="mt-2 space-y-1.5">
                    {(NEXT_STEPS[latest.kind] ?? NEXT_STEPS.video).map(step => (
                      <li
                        key={step}
                        className="text-muted-foreground flex items-start gap-2 text-xs"
                      >
                        <ArrowRight className="mt-0.5 size-3 shrink-0 text-orange-500" />
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {!busy &&
            !latest &&
            (idleSamples ?? (
              <div className="border-border text-muted-foreground flex h-64 items-center justify-center rounded-xl border border-dashed text-sm">
                Your result appears here.
              </div>
            ))}
        </div>
      </div>

      {inlineHistory && isSignedIn && history.length > 0 && (
        <section
          className="border-border mt-8 border-t pt-6"
          aria-label="Generation history"
        >
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <p className="text-[10px] font-semibold tracking-[0.3em] text-orange-500 uppercase">
                History
              </p>
              <h3 className="text-foreground mt-1 font-sans text-xl">
                Your previous runs
              </h3>
            </div>
            <p className="text-muted-foreground hidden text-xs sm:block">
              Select a result to view it full size
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {history.slice(0, 12).map(h => (
              <button
                key={h.id}
                type="button"
                onClick={() => setHistoryPreview(h)}
                className="group border-border bg-muted/20 overflow-hidden rounded-xl border text-left transition hover:-translate-y-0.5 hover:border-orange-500/60 hover:shadow-lg focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:outline-none"
              >
                <div className="relative aspect-video overflow-hidden bg-black/30">
                  {h.kind === 'image' ? (
                    <img
                      src={assetMediaSrc(h.url)}
                      alt="Generated result"
                      className="size-full object-cover transition duration-300 group-hover:scale-[1.03]"
                    />
                  ) : h.kind === 'audio' || h.kind === 'text' ? (
                    <span className="text-muted-foreground flex size-full items-center justify-center text-xs font-medium capitalize">
                      {h.kind}
                    </span>
                  ) : (
                    <video
                      src={assetMediaSrc(h.url)}
                      className="size-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                    />
                  )}
                  <span className="absolute right-2 bottom-2 grid size-7 place-items-center rounded-full bg-black/65 text-white opacity-0 backdrop-blur transition group-hover:opacity-100 group-focus-visible:opacity-100">
                    <Maximize2 className="size-3.5" />
                  </span>
                </div>
                <div className="p-3">
                  <p className="text-foreground truncate text-xs font-medium">
                    {h.prompt || 'Generated result'}
                  </p>
                  <p className="text-muted-foreground mt-1 text-[10px]">
                    {h.createdAt
                      ? new Intl.DateTimeFormat(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        }).format(new Date(h.createdAt))
                      : h.kind}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}
      {inlineHistory && <FailedToolRuns runs={failedRuns} />}
    </div>
  )
}
